import {
  Alert,
  AlertIcon,
  Box,
  Grid,
  Input,
  Select,
  VStack,
} from '@chakra-ui/react';

import {
  parseDateString,
  formatDateToString,
} from 'components/fields/auto/resolvers/resolverUtils';
import type { Membership } from 'api';
import type { DriverMembershipsProps } from './types';
import { useAnswerCodes } from '../../hooks/useAnswerCodes';
import { ValidatedField, ValidatedDateField } from '../../components';

const emptyMembership: Membership = {
  membership_group: '',
  member_number: '',
  member_date: null,
};

const DriverMemberships = ({
  driver,
  index,
  isLockedByOtherDriver,
  lockedByDriverName,
  onMembershipsChange,
}: DriverMembershipsProps) => {
  const { getOptions, isLoading } = useAnswerCodes();
  const primaryGroupOptions = getOptions(
    'membership_discount_membership_group',
  ).filter((opt) => opt.value);
  const secondaryGroupOptions = getOptions(
    'additional_membership_discount_membership_group',
  ).filter((opt) => opt.value);

  const memberships: Membership[] = [
    driver.memberships?.[0] || emptyMembership,
    driver.memberships?.[1] || emptyMembership,
  ];

  const isDisabled = isLockedByOtherDriver;

  const handleMembershipChange = (
    membershipIndex: number,
    field: keyof Membership,
    value: string | null,
  ) => {
    const updated = [...memberships];
    updated[membershipIndex] = {
      ...updated[membershipIndex],
      [field]: value,
    };
    onMembershipsChange(index, updated);
  };

  if (isLockedByOtherDriver && lockedByDriverName) {
    return (
      <Alert status="info" size="sm" borderRadius="md">
        <AlertIcon />
        Membership entered for {lockedByDriverName}
      </Alert>
    );
  }

  return (
    <VStack spacing={3} align="stretch">
      {/* Membership 1 */}
      <Box>
        <ValidatedField
          itemPath="driver"
          fieldPath="memberships.0.membership_group"
          label="Group"
          isDisabled={isDisabled || isLoading}
          labelSize="xs"
          labelProps={{ m: 0, mb: 1 }}
        >
          <Select
            variant="main"
            size="sm"
            value={memberships[0].membership_group || ''}
            onChange={(e) =>
              handleMembershipChange(0, 'membership_group', e.target.value)
            }
          >
            <option value="">None</option>
            {primaryGroupOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </ValidatedField>
        <Grid templateColumns="1fr 1fr" gap={2} mt={2}>
          <ValidatedField
            itemPath="driver"
            fieldPath="memberships.0.member_number"
            label="Number"
            isDisabled={isDisabled}
            labelSize="xs"
            labelProps={{ m: 0, mb: 1 }}
          >
            <Input
              variant="main"
              size="sm"
              value={memberships[0].member_number}
              onChange={(e) =>
                handleMembershipChange(0, 'member_number', e.target.value)
              }
              placeholder="#"
            />
          </ValidatedField>
          <ValidatedDateField
            itemPath="driver"
            fieldPath="memberships.0.member_date"
            label="Date"
            date={parseDateString(memberships[0].member_date)}
            onChange={(date: Date | null) =>
              handleMembershipChange(0, 'member_date', formatDateToString(date))
            }
            isDisabled={isDisabled}
            labelProps={{ fontSize: 'xs', m: 0, mb: 1 }}
          />
        </Grid>
      </Box>

      {/* Membership 2 */}
      <Box>
        <ValidatedField
          itemPath="driver"
          fieldPath="memberships.1.membership_group"
          label="Additional Group"
          isDisabled={isDisabled || isLoading}
          labelSize="xs"
          labelProps={{ m: 0, mb: 1 }}
        >
          <Select
            variant="main"
            size="sm"
            value={memberships[1].membership_group || ''}
            onChange={(e) =>
              handleMembershipChange(1, 'membership_group', e.target.value)
            }
          >
            <option value="">None</option>
            {secondaryGroupOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </ValidatedField>
        <Grid templateColumns="1fr 1fr" gap={2} mt={2}>
          <ValidatedField
            itemPath="driver"
            fieldPath="memberships.1.member_number"
            label="Number"
            isDisabled={isDisabled}
            labelSize="xs"
            labelProps={{ m: 0, mb: 1 }}
          >
            <Input
              variant="main"
              size="sm"
              value={memberships[1].member_number}
              onChange={(e) =>
                handleMembershipChange(1, 'member_number', e.target.value)
              }
              placeholder="#"
            />
          </ValidatedField>
          <ValidatedDateField
            itemPath="driver"
            fieldPath="memberships.1.member_date"
            label="Date"
            date={parseDateString(memberships[1].member_date)}
            onChange={(date: Date | null) =>
              handleMembershipChange(1, 'member_date', formatDateToString(date))
            }
            isDisabled={isDisabled}
            labelProps={{ fontSize: 'xs', m: 0, mb: 1 }}
          />
        </Grid>
      </Box>
    </VStack>
  );
};

export default DriverMemberships;
